import { useState } from 'react';
import { toast } from 'react-toastify';
import {
  notificationEventLabel,
  PICKUP_EVENT,
  useLoadNotifications,
  useResendLoadNotification,
  wasDelivered,
} from '../../features/order-notifications/api';
import type { LoadNotification } from '../../features/order-notifications/api';

function statusChip(n: LoadNotification) {
  const label =
    n.status === 'FAILED'
      ? 'Failed'
      : n.event === PICKUP_EVENT && n.openedAt
        ? 'Opened'
        : n.event === PICKUP_EVENT && n.deliveredAt
          ? 'Delivered'
          : wasDelivered(n)
            ? 'Sent'
            : 'Pending';
  const success = label === 'Sent' || label === 'Delivered' || label === 'Opened';
  const tone = success ? '#166534' : n.status === 'FAILED' ? '#991b1b' : '#854d0e';
  const background = success ? '#dcfce7' : n.status === 'FAILED' ? '#fee2e2' : '#fef9c3';

  return (
    <span
      style={{
        background,
        color: tone,
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {label}
    </span>
  );
}

export function EmailHistoryDialog({
  itemId,
  loadNumber,
  onClose,
}: {
  itemId: string;
  loadNumber?: string | null;
  onClose: () => void;
}) {
  // Refresh this dialog while it is open as the webhook updates delivery/open state.
  const q = useLoadNotifications(itemId, true, 15000);
  const resend = useResendLoadNotification(itemId);
  const [resendingId, setResendingId] = useState<string | null>(null);

  const notifications = q.data?.notifications ?? [];

  const handleResend = async (n: LoadNotification) => {
    setResendingId(n.id);
    try {
      await resend.mutateAsync(n.id);
      toast.success(`${notificationEventLabel(n.event)} resent to ${n.recipient}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to resend the email');
    } finally {
      setResendingId(null);
    }
  };

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="email-history-title">
      <div className="modal__backdrop" onClick={onClose} />
      <div className="modal__panel" style={{ maxWidth: 720, width: '100%' }}>
        <div className="modal__header">
          <div>
            <h3 id="email-history-title" className="modal__title">
              Email history
            </h3>
            <div className="muted tiny">
              {loadNumber ? `Load ${loadNumber}` : `Item ${itemId.slice(-6)}`}
              {q.isFetching ? ' · Refreshing…' : ''}
            </div>
          </div>
          <button className="btn btn--ghost" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="modal__body">
          {q.isLoading ? (
            <div className="skeleton" style={{ height: 180 }} />
          ) : q.isError ? (
            <div className="banner banner--warn">Failed to load the email history.</div>
          ) : notifications.length === 0 ? (
            <div className="muted">
              No emails have been sent to the customer about this load yet.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {notifications.map((n) => (
                <div key={n.id} className="card" style={{ padding: 12 }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 10,
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ minWidth: 0, flex: '1 1 auto' }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ fontWeight: 700 }}>{notificationEventLabel(n.event)}</span>
                        {statusChip(n)}
                      </div>
                      <div className="muted tiny" style={{ marginTop: 4 }}>
                        {new Date(n.sentAt ?? n.createdAt).toLocaleString()} · {n.recipient}
                      </div>
                      {n.event === PICKUP_EVENT && wasDelivered(n) && (
                        <div className="muted tiny" style={{ marginTop: 4 }}>
                          {n.deliveredAt
                            ? `Delivered ${new Date(n.deliveredAt).toLocaleString()}`
                            : 'Delivery not confirmed'}
                          {' · '}
                          {n.openedAt
                            ? `Opened ${new Date(n.openedAt).toLocaleString()}`
                            : n.machineOpenedAt
                              ? 'Privacy-protected auto-open detected; human open not confirmed'
                              : 'No open detected'}
                        </div>
                      )}
                      {n.error && (
                        <div className="muted tiny" style={{ marginTop: 4, color: '#991b1b' }}>
                          {n.error}
                        </div>
                      )}
                    </div>

                    <button
                      className="btn btn--secondary btn--sm"
                      onClick={() => handleResend(n)}
                      disabled={resend.isPending}
                      style={{ flex: '0 0 auto', whiteSpace: 'nowrap' }}
                    >
                      {resendingId === n.id ? 'Sending…' : 'Resend'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
